import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './MobilePlayerList.css';
import { useNavigate, useParams } from 'react-router-dom';
import { getApiBaseUrl } from '../utils/apiConfig';

/**
 * 移动端球员列表页面组件
 * 显示所有球员信息，支持搜索和筛选
 * 
 * @returns {JSX.Element} 移动端球员列表页面组件
 */
const MobilePlayerList = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [players, setPlayers] = useState([]);
  const [filteredPlayers, setFilteredPlayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore] = useState(true); // setHasMore 未使用
  const pageSize = 20; // 每页显示20个球员
  
  // API 基础URL
  const baseUrl = getApiBaseUrl();
  
  const navigate = useNavigate();
  const { zone } = useParams(); // 获取URL中的zone参数
  
  useEffect(() => {
    // 从后端API获取球员数据
    const fetchPlayers = async () => {
      try {
        setLoading(true);
        console.log('开始获取球员数据，区域:', zone);
        
        // 构建API请求URL，使用区域参数
        const apiUrl = zone ? `${baseUrl}/api/${zone}/players` : `${baseUrl}/api/players`;
        
        // 调用后端API获取球员列表
        const response = await axios.get(apiUrl, {
          params: {
            page: currentPage,
            limit: pageSize
          },
          headers: {
            'Cache-Control': 'no-cache',
            'Pragma': 'no-cache',
            'Expires': '0'
          }
        });
        
        if (response.data && response.data.success) {
          const playerData = response.data.data || [];
          console.log('球员数据获取成功:', playerData);
          
          // 处理球员数据，确保所有必要的字段都存在
          const processedPlayers = playerData.map(player => {
            // 确保ID字段存在且有效
            const playerId = player._id || player.id;
            if (!playerId) {
              console.error('球员数据缺少ID:', player);
              return null;
            }
            
            return {
              _id: playerId, // 保持一致使用_id
              name: player.name || '未命名',
              level: player.level || '未分级',
              gender: player.gender || '未知',
              avatar: player.avatar || null,
              singles: {
                matches: player.stats?.singles?.matches || 0,
                wins: player.stats?.singles?.wins || 0,
                losses: player.stats?.singles?.losses || 0,
                rating: player.stats?.singles?.rating || 1500,
                tier: player.stats?.singles?.tier || 'NEW',
                winRate: player.stats?.singles?.matches > 0 
                  ? ((player.stats.singles.wins / player.stats.singles.matches) * 100).toFixed(1) 
                  : '0.0'
              },
              doubles: {
                matches: player.stats?.doubles?.matches || 0,
                wins: player.stats?.doubles?.wins || 0,
                losses: player.stats?.doubles?.losses || 0,
                rating: player.stats?.doubles?.rating || 1500,
                tier: player.stats?.doubles?.tier || 'NEW',
                winRate: player.stats?.doubles?.matches > 0 
                  ? ((player.stats.doubles.wins / player.stats.doubles.matches) * 100).toFixed(1) 
                  : '0.0'
              }
            };
          }).filter(player => player !== null); // 过滤掉无效的球员数据
          
          // 按单打积分从高到低排序
          const sortedPlayers = processedPlayers.sort((a, b) => {
            const aRating = a.singles?.rating || 1500;
            const bRating = b.singles?.rating || 1500;
            // 如果单打积分相同，按双打积分排序
            if (bRating === aRating) {
              const aDoublesRating = a.doubles?.rating || 1500;
              const bDoublesRating = b.doubles?.rating || 1500;
              return bDoublesRating - aDoublesRating;
            }
            return bRating - aRating;
          });
          
          console.log('处理后的球员数据（已排序）:', sortedPlayers);
          setPlayers(sortedPlayers);
          setFilteredPlayers(sortedPlayers);
        } else {
          throw new Error('获取球员数据失败');
        }
        
        setLoading(false);
      } catch (err) {
        console.error('获取球员数据失败:', err);
        setError('获取球员数据失败: ' + (err.message || '未知错误'));
        setLoading(false);
      }
    };

    fetchPlayers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, zone]);

  // 搜索过滤
  const handleSearch = (e) => {
    const term = e.target.value;
    setSearchTerm(term);
    
    filterPlayers(term);
  };

  // 综合筛选
  const filterPlayers = (term) => {
    let filtered = [...players];
    
    // 搜索名称
    if (term.trim() !== '') {
      filtered = filtered.filter(player => 
        player.name.toLowerCase().includes(term.toLowerCase())
      );
    }
    
    setFilteredPlayers(filtered);
  };

  const handlePlayerClick = (playerId) => {
    console.log('点击球员:', playerId, '区域:', zone);
    // 检查playerId是否有效
    if (!playerId || playerId === 'undefined') {
      console.error('无效的球员ID:', playerId);
      return;
    }
    
    // 检查zone是否有效
    if (!zone) {
      console.error('缺少区域参数');
      return;
    }
    
    // 导航到球员详情页面，必须包含zone参数
    navigate(`/badminton/${zone}/players/${playerId}`);
  };

  // 未使用的函数，已注释
  // const getLevelClassName = (level) => {
  //   switch(level) {
  //     case '专业': return 'level-pro';
  //     case '高级': return 'level-a';
  //     case '中级': return 'level-b';
  //     case '初级': return 'level-c';
  //     default: return 'level-c';
  //   }
  // };

  // const getTierClassName = (tier) => {
  //   switch(tier) {
  //     case 'S': return 'tier-s';
  //     case 'T1': return 'tier-t1';
  //     case 'T2': return 'tier-t2';
  //     case 'T3': return 'tier-t3';
  //     case 'NEW': return 'tier-new';
  //     default: return 'tier-new';
  //   }
  // };

  if (loading) {
    console.log('显示加载中...');
    return <div className="mobile-loading">加载中...</div>;
  }

  if (error) {
    console.log('显示错误:', error);
    return <div className="mobile-error">{error}</div>;
  }

  return (
    <div className="mobile-player-list">
      <div className="mobile-player-header">
        <h1>球员列表</h1>
        <button 
          className="add-player-button"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            // 添加日志调试
            console.log('添加球员按钮被点击，当前区域:', zone);
            if (zone) {
              navigate(`/badminton/${zone}/players/create`);
            } else {
              navigate('/players/create');
            }
          }}
        >
          添加球员
        </button>
      </div>
      
      <div className="mobile-player-search">
        <input
          type="text"
          placeholder="搜索球员..."
          value={searchTerm}
          onChange={handleSearch}
          className="mobile-player-search-input"
        />
      </div>
      
      <div className="mobile-player-cards">
        {filteredPlayers.length === 0 ? (
          <div className="mobile-no-players">未找到匹配的球员</div>
        ) : (
          filteredPlayers.map(player => (
            <div 
              key={player._id} 
              className="mobile-player-card"
              onClick={() => handlePlayerClick(player._id)}
            >
              <div className="player-basic-info">
                <div className="player-name-level">
                  <div className="player-name-gender">
                    <span className="player-name">{player.name}</span>
                    <span className={`player-gender ${player.gender === 'FEMALE' ? 'female' : 'male'}`}>
                      {player.gender === 'FEMALE' ? '女' : '男'}
                    </span>
                    <span className="player-matches">
                      {player.singles.matches + player.doubles.matches}场
                    </span>
                  </div>
                </div>
                
                <div className="player-ratings">
                  <div className="rating-item">
                    <span className="rating-label">单</span>
                    <span className="rating-value">{Math.round(player.singles.rating)}</span>
                  </div>
                  <div className="rating-item">
                    <span className="rating-label">双</span>
                    <span className="rating-value">{Math.round(player.doubles.rating)}</span>
                  </div>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
      
      {hasMore && (
        <div 
          className="mobile-load-more"
          onClick={() => setCurrentPage(prev => prev + 1)}
        >
          <span>加载更多</span>
          <i className="fas fa-chevron-down"></i>
        </div>
      )}
    </div>
  );
};

export default MobilePlayerList; 